import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages graph viewport state, selection sync, expand/collapse state, and auto-fit behavior.
 */
export const useGraphViewport = ({ graphState }) => {
  const reactFlowInstanceRef = useRef(null);
  const fitViewTimerRef = useRef(null);
  const pendingAutoFitRef = useRef(false);
  const pendingAutoFitOptionsRef = useRef(null);

  const [layoutDirection, setLayoutDirection] = useState('LR');
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [viewMode, setViewMode] = useState('list');
  const [expandedNodeIds, setExpandedNodeIds] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const runPendingAutoFit = useCallback(() => {
    if (viewMode !== 'graph') return false;

    const instance = reactFlowInstanceRef.current;
    if (!instance) return false;

    const options = pendingAutoFitOptionsRef.current || {};
    instance.fitView({
      padding: 0.4,
      duration: 500,
      includeHiddenNodes: true,
      minZoom: 0.2,
      maxZoom: 0.88,
      ...options,
    });
    return true;
  }, [viewMode]);

  const requestAutoFit = useCallback(
    (options = {}) => {
      pendingAutoFitRef.current = true;
      pendingAutoFitOptionsRef.current = options;

      if (typeof window === 'undefined') return;
      if (fitViewTimerRef.current) {
        window.clearTimeout(fitViewTimerRef.current);
      }

      fitViewTimerRef.current = window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (pendingAutoFitRef.current && runPendingAutoFit()) {
              pendingAutoFitRef.current = false;
            }
          });
        });
      }, 100);
    },
    [runPendingAutoFit],
  );

  const applyLayoutDirection = useCallback(
    (nextLayoutDirection) => {
      if (!nextLayoutDirection) return;

      setLayoutDirection((current) => {
        if (current === nextLayoutDirection) return current;
        return nextLayoutDirection;
      });
      setLayoutVersion((value) => value + 1);
      requestAutoFit({ padding: 0.38, duration: 420, maxZoom: 0.9 });
    },
    [requestAutoFit],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (!pendingAutoFitRef.current || viewMode !== 'graph') return undefined;

    const timer = window.setTimeout(() => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (pendingAutoFitRef.current && runPendingAutoFit()) {
            pendingAutoFitRef.current = false;
          }
        });
      });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [layoutDirection, layoutVersion, runPendingAutoFit, viewMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    return () => {
      if (fitViewTimerRef.current) {
        window.clearTimeout(fitViewTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const rootNodeId = graphState.rootNodeId;
    if (!rootNodeId) {
      setExpandedNodeIds([]);
      return;
    }

    setExpandedNodeIds((previous) =>
      previous.includes(rootNodeId) ? previous : [rootNodeId, ...previous],
    );
  }, [graphState.rootNodeId]);

  useEffect(() => {
    const nodeIds = new Set(Object.keys(graphState.nodesById || {}));
    setExpandedNodeIds((previous) => previous.filter((nodeId) => nodeIds.has(nodeId)));

    if (selectedNodeId && !nodeIds.has(selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [graphState.nodesById, selectedNodeId]);

  const toggleExpandedNode = useCallback((nodeId) => {
    setExpandedNodeIds((previous) =>
      previous.includes(nodeId)
        ? previous.filter((id) => id !== nodeId)
        : [...previous, nodeId],
    );
  }, []);

  const focusNodeInGraph = useCallback(
    (nodeId) => {
      if (!nodeId) return;

      setSelectedNodeId(nodeId);
      setViewMode('graph');
      requestAutoFit({
        nodes: [{ id: nodeId }],
        padding: 0.5,
        duration: 420,
        maxZoom: 0.92,
      });
    },
    [requestAutoFit],
  );

  const handleGraphInit = useCallback(
    (instance) => {
      reactFlowInstanceRef.current = instance;
      if (pendingAutoFitRef.current) {
        window.requestAnimationFrame(() => {
          if (pendingAutoFitRef.current && runPendingAutoFit()) {
            pendingAutoFitRef.current = false;
          }
        });
      }
    },
    [runPendingAutoFit],
  );

  const applyAutoLayout = useCallback(() => {
    setLayoutVersion((value) => value + 1);
    requestAutoFit({ padding: 0.38, duration: 420, maxZoom: 0.9 });
    return true;
  }, [requestAutoFit]);

  return {
    layoutDirection,
    layoutVersion,
    viewMode,
    expandedNodeIds,
    selectedNodeId,
    setLayoutDirection: applyLayoutDirection,
    setViewMode,
    setExpandedNodeIds,
    setSelectedNodeId,
    requestAutoFit,
    toggleExpandedNode,
    focusNodeInGraph,
    handleGraphInit,
    applyAutoLayout,
  };
};

export default useGraphViewport;
